// Serializable config for a single comparison plot. Stored inside the report
// layout as a plot block (view_config.metricLayout). Free of React/runtime deps
// so the shared layout util, the persistence parser, and the plot registry can
// all import it without a module cycle.

export const COMPARISON_PLOT_TYPES = ["scatter"] as const

export type ComparisonPlotType = (typeof COMPARISON_PLOT_TYPES)[number]

// Axis / bubble-size scales. "log" is bi-symmetric log (tolerates zero and
// negatives); "pow" uses the per-axis exponent. Absence === "linear".
export const COMPARISON_PLOT_SCALES = ["linear", "log", "sqrt", "pow"] as const

export type ComparisonPlotScale = (typeof COMPARISON_PLOT_SCALES)[number]

// Fields every plot type shares; per-type fields live on the variants so the
// compiler rejects, say, a scatter-only field on a future histogram config.
interface ComparisonPlotBase {
  id: string
  title?: string
}

export interface ScatterPlotConfig extends ComparisonPlotBase {
  type: "scatter"
  xAxisMetricId?: string | null
  yAxisMetricId?: string | null
  zAxisMetricId?: string | null
  showTrendLine?: boolean
  // Legend of Compare By groups; only meaningful while a Compare By is active.
  showLegend?: boolean
  // Drop Tukey-IQR outliers from the view, the axis fit, and the trend fit.
  excludeOutliers?: boolean
  // Tukey fence width (k×IQR) for the outlier filter; absent === 1.5 (inner fence).
  outlierIqrMultiplier?: number
  // Per-axis and bubble-size scales; absent === "linear".
  xAxisScale?: ComparisonPlotScale
  yAxisScale?: ComparisonPlotScale
  zAxisScale?: ComparisonPlotScale
  // Exponent for a "pow" scale on the matching axis; ignored for other scales.
  xAxisScaleExponent?: number
  yAxisScaleExponent?: number
  zAxisScaleExponent?: number
}

// Discriminated on `type`, which every saved config already carries. Future
// plot types add a variant here (and a case in the persistence parser).
export type ComparisonPlotConfig = ScatterPlotConfig
